<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Activities</h5>
        <p class="page-sub">Track calls, meetings, emails and tasks</p>
      </div>
      <div class="header-actions">
        <div class="export-wrap" ref="exportWrap">
          <button class="btn-export" @click="exportMenuOpen = !exportMenuOpen" :disabled="exporting">
            <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" style="font-size:10px" />
          </button>
          <div v-if="exportMenuOpen" class="export-menu">
            <button class="export-opt" @click="openExportModal('csv')"><font-awesome-icon icon="file-csv" class="me-2" />Export CSV</button>
            <button class="export-opt" @click="openExportModal('pdf')"><font-awesome-icon icon="file-pdf" class="me-2" />Export PDF</button>
          </div>
        </div>
        <button v-if="permission?.create_crm_activities" class="btn-primary" @click="openCreate">
          <font-awesome-icon icon="plus" class="me-1" /> Log Activity
        </button>
      </div>
    </div>

    <div class="filter-bar">
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="page = 1; fetchActivities()">
          <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
      <input v-model="search" @input="debouncedFetch" class="filter-input" placeholder="Search by subject..." />
      <select v-model="filterType" @change="page=1; fetchActivities()" class="filter-select">
        <option value="">All Types</option>
        <option value="call">Call</option>
        <option value="meeting">Meeting</option>
        <option value="email">Email</option>
        <option value="task">Task</option>
        <option value="note">Note</option>
      </select>
      <select v-model="filterStatus" @change="page=1; fetchActivities()" class="filter-select">
        <option value="">All Statuses</option>
        <option value="pending">Pending</option>
        <option value="completed">Completed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <input type="date" v-model="dateFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="dateTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="applyDateFilter">Apply</button>
      <button class="btn-clear" @click="clearDateFilter" v-if="dateFrom || dateTo">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading activities...</p></div>
      <div v-else-if="!activities.length" class="empty-state">
        <font-awesome-icon icon="calendar-check" class="empty-icon" />
        <p>No activities found</p>
        <span>Log calls, meetings and tasks to track customer interactions</span>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr><th>Type</th><th>Subject</th><th>Related To</th><th>Due Date</th><th>Status</th><th>Created</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="a in activities" :key="a.id">
            <td><span :class="['type-badge', `type--${a.type}`]">{{ a.type }}</span></td>
            <td class="name-col">{{ a.subject }}</td>
            <td>{{ a.subject_label || '—' }}</td>
            <td>{{ a.due_at ? fmtDate(a.due_at) : '—' }}</td>
            <td><span :class="['status-badge', `status--${a.status}`]">{{ a.status }}</span></td>
            <td>{{ fmtDate(a.created_at) }}</td>
            <td>
              <div class="row-actions">
                <button v-if="permission?.edit_crm_activities && a.status === 'pending'" class="btn-link btn-link--complete" @click="markComplete(a)">Complete</button>
                <button class="btn-link btn-link--view" @click="openView(a)">View</button>
                <button v-if="permission?.edit_crm_activities && a.status !== 'completed'" class="btn-link btn-link--edit" @click="openEdit(a)">Edit</button>
                <button v-if="permission?.edit_crm_activities" class="btn-link btn-link--delete" @click="deleteTarget = a">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="meta" class="pagination-bar">
        <span class="pagination-info">{{ meta.from ?? 1 }}–{{ meta.to ?? meta.total }} of {{ meta.total }} records</span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(1)">«</button>
          <button class="pg-btn" :disabled="meta.current_page <= 1" @click="changePage(meta.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === meta.current_page }" @click="changePage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="meta.current_page >= meta.last_page" @click="changePage(meta.last_page)">»</button>
        </div>
      </div>
    </div>

    <div class="modal-overlay" :class="{ open: modalOpen }" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ editing ? 'Edit Activity' : 'Log Activity' }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Type <span class="req">*</span></label>
                <select v-model="form.type" class="form-input" required>
                  <option value="call">Call</option>
                  <option value="meeting">Meeting</option>
                  <option value="email">Email</option>
                  <option value="task">Task</option>
                  <option value="note">Note</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Due Date/Time</label>
                <input v-model="form.due_at" type="datetime-local" class="form-input" />
              </div>
            </div>
            <div class="form-group form-group--full">
              <label class="form-label">Subject <span class="req">*</span></label>
              <input v-model="form.subject" class="form-input" required />
            </div>

            <!-- Related To -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Related To</label>
                <select v-model="form.subject_type" class="form-input" @change="onSubjectTypeChange">
                  <option value="">— None —</option>
                  <option value="lead">Lead</option>
                  <option value="contact">Contact</option>
                  <option value="customer">Account</option>
                  <option value="opportunity">Opportunity</option>
                </select>
              </div>
              <div class="form-group" v-if="form.subject_type">
                <label class="form-label">Search {{ relatedTypeLabel }}</label>
                <div class="typeahead-wrap">
                  <div v-if="form.subject_id" class="typeahead-selected">
                    <span>{{ form.subject_label }}</span>
                    <button type="button" class="typeahead-clear" @click="clearRelated">&times;</button>
                  </div>
                  <template v-else>
                    <input
                      v-model="relatedSearch"
                      @input="debouncedSearchRelated"
                      class="form-input"
                      :placeholder="`Type to search ${relatedTypeLabel}…`"
                      autocomplete="off"
                    />
                    <div v-if="relatedResults.length" class="typeahead-dropdown">
                      <div
                        v-for="r in relatedResults" :key="r.id"
                        class="typeahead-item"
                        @mousedown.prevent="selectRelated(r)"
                      >{{ r._label }}</div>
                    </div>
                    <div v-else-if="relatedLoading" class="typeahead-dropdown">
                      <div class="typeahead-item typeahead-item--muted">Searching…</div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="form-group form-group--full">
              <label class="form-label">Notes</label>
              <textarea v-model="form.notes" class="form-input" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="submitting">{{ submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Log Activity') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div class="modal-overlay" :class="{ open: !!deleteTarget }" @click.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--red"><font-awesome-icon icon="trash" /></div>
          <div>
            <span class="modal-title">Delete Activity</span>
            <p class="modal-sub">This cannot be undone</p>
          </div>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <span :class="['type-badge', `type--${deleteTarget?.type}`]">{{ deleteTarget?.type }}</span>
            <p class="action-name">{{ deleteTarget?.subject }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="submitting">
            {{ submitting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Mark Complete Confirm -->
    <div class="modal-overlay" :class="{ open: !!completeTarget }" @click.self="completeTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <div class="modal-header-icon"><font-awesome-icon icon="check-circle" /></div>
          <div>
            <span class="modal-title">Mark as Complete</span>
            <p class="modal-sub">This will close the activity</p>
          </div>
          <button class="modal-close" @click="completeTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="action-card">
            <span :class="['type-badge', `type--${completeTarget?.type}`]">{{ completeTarget?.type }}</span>
            <p class="action-name">{{ completeTarget?.subject }}</p>
          </div>
          <p class="action-note">The activity status will be set to <strong>Completed</strong> and cannot be reverted from here.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="completeTarget = null">Cancel</button>
          <button class="btn-complete" @click="doComplete" :disabled="submitting">
            <font-awesome-icon icon="check" class="me-1" />
            {{ submitting ? 'Saving…' : 'Mark Complete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- View Drawer -->
    <div class="drawer-overlay" :class="{ open: viewDrawerOpen }" @click.self="closeView">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <span v-if="viewTarget" :class="['type-badge', `type--${viewTarget.type}`]">{{ viewTarget.type }}</span>
            <span class="drawer-title">{{ viewTarget?.subject || 'Activity' }}</span>
          </div>
          <button class="modal-close" @click="closeView"><font-awesome-icon icon="times" /></button>
        </div>

        <div v-if="viewLoading" class="drawer-loading"><div class="spinner"></div><p>Loading…</p></div>

        <div v-else-if="viewTarget" class="drawer-body">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span :class="['status-badge', `status--${viewTarget.status}`]">{{ viewTarget.status }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Due Date</span>
              <span class="detail-value">{{ viewTarget.due_at ? fmtDate(viewTarget.due_at) : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Related To</span>
              <span class="detail-value">{{ viewTarget.subject_label || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Assigned To</span>
              <span class="detail-value">{{ viewTarget.assignedTo ? `${viewTarget.assignedTo.first_name ?? ''} ${viewTarget.assignedTo.last_name ?? ''}`.trim() || '—' : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Created</span>
              <span class="detail-value">{{ fmtDate(viewTarget.created_at) }}</span>
            </div>
          </div>

          <div v-if="viewTarget.description" class="detail-desc">
            <span class="detail-label">Description</span>
            <p class="detail-desc-text">{{ viewTarget.description }}</p>
          </div>

          <div class="notes-section">
            <div class="notes-header">Notes <span class="notes-count">{{ viewTarget.notes?.length ?? 0 }}</span></div>
            <div v-if="!viewTarget.notes?.length" class="notes-empty">No notes yet — be the first to add one.</div>
            <div v-else class="notes-list">
              <div v-for="n in viewTarget.notes" :key="n.id" class="note-item">
                <div class="note-meta">
                  <span class="note-author">{{ n.created_by_name || 'Unknown' }}</span>
                  <span class="note-time">{{ fmtDate(n.created_at) }}</span>
                </div>
                <p class="note-body">{{ n.body }}</p>
              </div>
            </div>
            <div class="note-form">
              <textarea v-model="noteBody" class="form-input note-textarea" rows="3" placeholder="Write a note…"></textarea>
              <div class="note-form-actions">
                <button class="btn-primary" @click="submitNote" :disabled="noteSubmitting || !noteBody.trim()">
                  {{ noteSubmitting ? 'Adding…' : 'Add Note' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Column-Picker Export Modal -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal=false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <span>Choose Columns to Export <span class="col-picker-badge">{{ selectedExportCols.length }}</span></span>
            <button class="col-picker-close" @click="showExportModal=false"><font-awesome-icon icon="times"/></button>
          </div>
          <div class="col-picker-body">
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols=exportColumnDefs.map(c=>c.key)">Select All</button>
              <button class="col-picker-link" @click="selectedExportCols=[]">Deselect All</button>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" />
                {{ col.label }}
              </label>
            </div>
          </div>
          <div class="col-picker-footer">
            <button class="btn-ghost" @click="showExportModal=false">Cancel</button>
            <button class="btn-primary" :disabled="!selectedExportCols.length||exporting" @click="doExport">
              <font-awesome-icon v-if="exporting" icon="spinner" spin class="me-1"/>
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
export default {
  setup() { return { toast: useToast(), store: useStore() }; },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
    pageRange() {
      if (!this.meta) return [];
      const cur = this.meta.current_page, last = this.meta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
    relatedTypeLabel() {
      return { lead: 'Lead', contact: 'Contact', customer: 'Account', opportunity: 'Opportunity' }[this.form.subject_type] ?? '';
    },
  },
  data() {
    return {
      activities: [], meta: null, loading: false, submitting: false,
      search: '', filterType: '', filterStatus: '', page: 1, length: 5,
      dateFrom: '', dateTo: '', exportMenuOpen: false, exporting: false,
      showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [],
      exportColumnDefs: [
        { key: 'type',       label: 'Type',       pdfWidth: 80  },
        { key: 'subject',    label: 'Subject',    pdfWidth: 200 },
        { key: 'related_to', label: 'Related To', pdfWidth: 160 },
        { key: 'due_at',     label: 'Due Date',   pdfWidth: 100 },
        { key: 'status',     label: 'Status',     pdfWidth: 90  },
        { key: 'created_at', label: 'Created',    pdfWidth: 131 },
      ],
      modalOpen: false, editing: null, completeTarget: null, deleteTarget: null,
      form: { type:'call', subject:'', due_at:'', notes:'', subject_type:'', subject_id:'', subject_label:'' },
      debounceTimer: null,
      relatedSearch: '', relatedResults: [], relatedLoading: false, relatedTimer: null,
      viewDrawerOpen: false, viewTarget: null, viewLoading: false,
      noteBody: '', noteSubmitting: false,
    };
  },
  mounted() { this.fetchActivities(); document.addEventListener('click', this.onOutsideClick); },
  beforeUnmount() { document.removeEventListener('click', this.onOutsideClick); },
  methods: {
    async fetchActivities() {
      this.loading = true;
      try {
        const { data } = await axios.get('/crm/activities', { params: { search: this.search, type: this.filterType, status: this.filterStatus, from: this.dateFrom || undefined, to: this.dateTo || undefined, page: this.page, length: this.length } });
        this.activities = data.data?.data ?? data.data ?? data ?? [];
        this.meta = data.data?.meta ?? data.meta ?? null;
      } catch { this.toast.error('Failed to load activities'); }
      finally { this.loading = false; }
    },
    debouncedFetch() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => { this.page = 1; this.fetchActivities(); }, 400); },
    changePage(p) { this.page = p; this.fetchActivities(); },
    applyDateFilter() { this.page = 1; this.fetchActivities(); },
    clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; this.page = 1; this.fetchActivities(); },
    openExportModal(format) {
      this.exportMenuOpen     = false;
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(r, key) {
      switch (key) {
        case 'type':       return r.type ?? '—';
        case 'subject':    return r.subject ?? '—';
        case 'related_to': return r.subject_label ?? r.subject_type ?? '—';
        case 'due_at':     return r.due_at ? new Date(r.due_at).toLocaleDateString('en-GB') : '—';
        case 'status':     return r.status ?? '—';
        case 'created_at': return r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—';
        default:           return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exporting = true; this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.search)       params.search = this.search;
        if (this.filterType)   params.type   = this.filterType;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.dateFrom)     params.from   = this.dateFrom;
        if (this.dateTo)       params.to     = this.dateTo;
        const { data } = await axios.get('/crm/activities', { params });
        const rows = data.data?.data ?? data.data ?? data ?? [];
        const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `activities-${date}.csv`; a.click(); URL.revokeObjectURL(url);
        } else {
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable = 761; const totalW = cols.reduce((s,c)=>s+c.pdfWidth,0); const scale = usable/totalW;
          const colStyles = {}; cols.forEach((c,i)=>{ colStyles[i]={cellWidth:c.pdfWidth*scale}; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Activities',40,46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`,40,62);
          doc.autoTable({ startY:78, head:[headers], body,
            styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'},
            headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5},
            alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
            didDrawPage(d){ doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`,doc.internal.pageSize.width/2,doc.internal.pageSize.height-16,{align:'center'}); },
          });
          doc.save(`activities-${date}.pdf`);
        }
      } catch { this.toast?.error('Export failed. Please try again.'); }
      finally { this.exporting = false; }
    },
    onOutsideClick(e) {
      if (this.$refs.exportWrap && !this.$refs.exportWrap.contains(e.target)) this.exportMenuOpen = false;
    },
    emptyForm() {
      return { type:'call', subject:'', due_at:'', notes:'', subject_type:'', subject_id:'', subject_label:'' };
    },
    openCreate() {
      this.editing = null;
      this.form = this.emptyForm();
      this.relatedSearch = ''; this.relatedResults = [];
      this.modalOpen = true;
    },
    openEdit(a) {
      this.editing = a.id;
      this.form = {
        type: a.type, subject: a.subject ?? '', due_at: a.due_at ?? '', notes: a.description ?? a.notes ?? '',
        subject_type: a.subject_type ?? '', subject_id: a.subject_id ?? '', subject_label: a.subject_label ?? '',
      };
      this.relatedSearch = ''; this.relatedResults = [];
      this.modalOpen = true;
    },
    closeModal() { this.modalOpen = false; },
    async submit() {
      this.submitting = true;
      try {
        if (this.editing) { await axios.put(`/crm/activities/${this.editing}`, this.form); this.toast.success('Activity updated'); }
        else              { await axios.post('/crm/activities', this.form); this.toast.success('Activity logged'); }
        this.closeModal(); this.fetchActivities();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save'); }
      finally { this.submitting = false; }
    },
    markComplete(a) { this.completeTarget = a; },
    async doComplete() {
      this.submitting = true;
      try {
        await axios.patch(`/crm/activities/${this.completeTarget.id}/complete`);
        this.toast.success('Activity marked complete');
        this.completeTarget = null;
        this.fetchActivities();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to update'); }
      finally { this.submitting = false; }
    },
    onSubjectTypeChange() {
      this.form.subject_id = ''; this.form.subject_label = '';
      this.relatedSearch = ''; this.relatedResults = [];
    },
    clearRelated() {
      this.form.subject_id = ''; this.form.subject_label = '';
      this.relatedSearch = ''; this.relatedResults = [];
    },
    debouncedSearchRelated() {
      clearTimeout(this.relatedTimer);
      this.relatedResults = [];
      if (!this.relatedSearch.trim()) return;
      this.relatedTimer = setTimeout(() => this.searchRelated(), 350);
    },
    async searchRelated() {
      const type = this.form.subject_type;
      if (!type) return;
      const endpointMap = {
        lead:        '/crm/leads',
        contact:     '/crm/contacts',
        customer:    '/crm/accounts',
        opportunity: '/crm/opportunities',
      };
      this.relatedLoading = true;
      try {
        const { data } = await axios.get(endpointMap[type], { params: { search: this.relatedSearch, per_page: 10 } });
        const rows = data.data?.data ?? data.data ?? [];
        this.relatedResults = rows.map(r => ({
          id:     r.id,
          _label: type === 'lead'
            ? (r.first_name || r.last_name ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() : r.company_name ?? r.email ?? r.id)
            : type === 'contact'
            ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || r.email
            : type === 'customer'
            ? (r.customer_name ?? r.name ?? r.id)
            : (r.name ?? r.id),
        }));
      } catch { /* silent */ }
      finally { this.relatedLoading = false; }
    },
    selectRelated(r) {
      this.form.subject_id    = r.id;
      this.form.subject_label = r._label;
      this.relatedResults     = [];
      this.relatedSearch      = '';
    },
    async doDelete() {
      this.submitting = true;
      try {
        await axios.delete(`/crm/activities/${this.deleteTarget.id}`);
        this.toast.success('Activity deleted');
        this.deleteTarget = null;
        this.fetchActivities();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete'); }
      finally { this.submitting = false; }
    },
    async openView(a) {
      this.viewDrawerOpen = true;
      this.viewLoading = true;
      this.viewTarget = null;
      this.noteBody = '';
      try {
        const { data } = await axios.get(`/crm/activities/detail/${a.id}`);
        this.viewTarget = data.data ?? data;
      } catch { this.toast.error('Failed to load activity'); this.viewDrawerOpen = false; }
      finally { this.viewLoading = false; }
    },
    closeView() { this.viewDrawerOpen = false; this.viewTarget = null; this.noteBody = ''; },
    async submitNote() {
      if (!this.noteBody.trim()) return;
      this.noteSubmitting = true;
      try {
        const { data } = await axios.post(`/crm/activities/${this.viewTarget.id}/notes`, { body: this.noteBody });
        const note = data.data ?? data;
        if (!this.viewTarget.notes) this.viewTarget.notes = [];
        this.viewTarget.notes.push(note);
        this.noteBody = '';
        this.toast.success('Note added');
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to add note'); }
      finally { this.noteSubmitting = false; }
    },
    fmtDate(v) { return new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
  }
};
</script>

<style scoped>
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:20px}
.page-title{font-size:20px;font-weight:700;color:#1a2b45;margin:0 0 3px}
.page-sub{font-size:13px;color:#7a90b0;margin:0}
.filter-bar{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}
.filter-input{padding:9px 13px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;flex:1;min-width:180px;outline:none}
.filter-input:focus{border-color:#004AAD}
.filter-select{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none}
.btn-primary{background:#004AAD;color:#fff;border:none;padding:9px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-primary:hover{background:#003b8e}
.btn-primary:disabled{opacity:.55;cursor:not-allowed}
.btn-ghost{background:#f4f7fb;color:#3d5068;border:1px solid #e4eaf2;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.card{background:#fff;border-radius:12px;border:1.5px solid #e8edf5;overflow:hidden}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:56px 24px;color:#7a90b0;gap:8px}
.empty-icon{font-size:36px;opacity:.4}
.spinner{width:28px;height:28px;border:3px solid #e8edf5;border-top-color:#004AAD;border-radius:50%;animation:spin .7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.data-table{width:100%;border-collapse:collapse;font-size:13px}
.data-table th{background:#f4f7fb;font-weight:600;color:#5a6a85;padding:11px 14px;text-align:left;white-space:nowrap}
.data-table td{padding:11px 14px;border-top:1px solid #f0f4fa;color:#1a2b45;vertical-align:middle}
.data-table tbody tr:hover{background:#f9fbff}
.name-col{font-weight:600}
.row-actions{display:flex;gap:8px}
.btn-link{background:none;border:none;font-size:12.5px;cursor:pointer;padding:0;font-weight:600}
.btn-link--edit{color:#004AAD}
.btn-link--complete{color:#166534}
.btn-link--delete{color:#dc2626}
.type-badge,.status-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:20px;padding:2px 9px;text-transform:capitalize}
.type--call{background:#e0f2fe;color:#075985}
.type--meeting{background:#ede9fe;color:#6d28d9}
.type--email{background:#fef3c7;color:#92400e}
.type--task{background:#dcfce7;color:#166534}
.type--note{background:#f3f4f6;color:#374151}
.status--pending{background:#fef3c7;color:#92400e}
.status--completed{background:#dcfce7;color:#166534}
.status--cancelled{background:#f3f4f6;color:#6b7280}
.per-page-wrap{display:flex;align-items:center;gap:8px;flex-shrink:0}
.per-page-label{font-size:13px;color:#5a6a85;white-space:nowrap}
.per-page-select{padding:7px 10px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#fff;outline:none;cursor:pointer}
.per-page-select:focus{border-color:#004AAD}
.pagination-bar{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:1px solid #f0f4fa;flex-wrap:wrap;gap:10px}
.pagination-info{font-size:12.5px;color:#7a90b0}
.pagination-controls{display:flex;align-items:center;gap:4px}
.pg-btn{padding:6px 10px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:13px;cursor:pointer}
.pg-btn:disabled{opacity:.4;cursor:not-allowed}
.pg-pages{display:flex;gap:3px}
.pg-num{width:30px;height:30px;border:1.5px solid #dde5f0;border-radius:7px;background:#fff;color:#1a2b45;font-size:12.5px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pg-num:hover{border-color:#004AAD;color:#004AAD}
.pg-num--active{background:#004AAD;border-color:#004AAD;color:#fff}
.modal-overlay{position:fixed;inset:0;z-index:1050;display:flex;align-items:center;justify-content:center;background:rgba(15,25,50,.45);backdrop-filter:blur(3px);opacity:0;pointer-events:none;transition:opacity .2s;padding:16px}
.modal-overlay.open{opacity:1;pointer-events:all}
.modal-box{background:#fff;border-radius:14px;width:100%;max-width:560px;box-shadow:0 20px 60px rgba(0,0,0,.14);animation:slideUp .2s ease;max-height:90vh;overflow-y:auto}
@keyframes slideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #e4eaf2}
.modal-title{font-size:16px;font-weight:700;color:#1a2b45}
.modal-close{width:28px;height:28px;border-radius:7px;border:1px solid #dde5f0;background:transparent;cursor:pointer;font-size:14px;color:#7a90b0;display:flex;align-items:center;justify-content:center}
.modal-body{padding:20px 22px}
.modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 22px;border-top:1px solid #f0f4fa}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:4px}
.form-group{display:flex;flex-direction:column;gap:5px;margin-bottom:14px}
.form-group--full{grid-column:1/-1}
.form-label{font-size:12.5px;font-weight:600;color:#1a2b45}
.form-input{padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;outline:none;width:100%;font-family:inherit}
.form-input:focus{border-color:#004AAD;box-shadow:0 0 0 3px rgba(0,74,173,.07)}
.req{color:#e03535}
.me-1{margin-right:4px}
.typeahead-wrap{position:relative}
.typeahead-selected{display:flex;align-items:center;justify-content:space-between;padding:9px 12px;border:1.5px solid #dde5f0;border-radius:8px;font-size:13px;color:#1a2b45;background:#f4f7fb}
.typeahead-clear{background:none;border:none;color:#7a90b0;cursor:pointer;font-size:16px;line-height:1;padding:0 0 0 8px}
.typeahead-clear:hover{color:#e03535}
.typeahead-dropdown{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1.5px solid #dde5f0;border-radius:8px;box-shadow:0 6px 20px rgba(0,0,0,.10);z-index:200;max-height:200px;overflow-y:auto}
.typeahead-item{padding:9px 13px;font-size:13px;color:#1a2b45;cursor:pointer}
.typeahead-item:hover{background:#f4f7fb}
.typeahead-item--muted{color:#a0aec0;cursor:default}
.modal-box--sm{max-width:420px}
.modal-header-icon{width:36px;height:36px;border-radius:9px;background:#dcfce7;color:#166534;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0}
.modal-header-icon--red{background:#fee2e2;color:#dc2626}
.btn-danger{background:#dc2626;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-danger:hover{background:#b91c1c}
.btn-danger:disabled{opacity:.55;cursor:not-allowed}
.modal-sub{font-size:12px;color:#7a90b0;margin:2px 0 0}
.action-card{background:#f4f7fb;border-radius:10px;padding:14px 16px;margin-bottom:14px}
.action-name{font-size:14px;font-weight:700;color:#1a2b45;margin:4px 0 0}
.action-note{font-size:13px;color:#5a6a85;margin:0}
.btn-complete{background:#166534;color:#fff;border:none;padding:8px 18px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.btn-complete:hover{background:#14532d}
.btn-complete:disabled{opacity:.55;cursor:not-allowed}
.btn-link--view{color:#5a6a85}
.btn-link--view:hover{color:#004AAD}
/* Drawer */
.drawer-overlay{position:fixed;inset:0;z-index:1060;background:rgba(15,25,50,.4);backdrop-filter:blur(2px);display:flex;justify-content:flex-end;opacity:0;pointer-events:none;transition:opacity .25s}
.drawer-overlay.open{opacity:1;pointer-events:all}
.drawer{width:480px;max-width:100vw;height:100%;background:#fff;display:flex;flex-direction:column;box-shadow:-8px 0 40px rgba(0,0,0,.12);transform:translateX(100%);transition:transform .25s ease;overflow:hidden}
.drawer-overlay.open .drawer{transform:translateX(0)}
.drawer-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid #e4eaf2;gap:12px;flex-shrink:0}
.drawer-title-group{display:flex;align-items:center;gap:10px;min-width:0}
.drawer-title{font-size:15px;font-weight:700;color:#1a2b45;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.drawer-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;flex:1;gap:10px;color:#7a90b0;font-size:13px}
.drawer-body{flex:1;overflow-y:auto;padding:20px}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px}
.detail-item{display:flex;flex-direction:column;gap:4px}
.detail-label{font-size:11px;font-weight:700;color:#7a90b0;text-transform:uppercase;letter-spacing:.4px}
.detail-value{font-size:13px;color:#1a2b45;font-weight:500}
.detail-desc{margin-bottom:18px}
.detail-desc-text{font-size:13px;color:#3d5068;margin:6px 0 0;line-height:1.6;white-space:pre-wrap}
.notes-section{border-top:1px solid #e8edf5;padding-top:18px}
.notes-header{font-size:13px;font-weight:700;color:#1a2b45;margin-bottom:14px;display:flex;align-items:center;gap:8px}
.notes-count{background:#e8edf5;color:#5a6a85;font-size:11px;font-weight:700;border-radius:20px;padding:1px 7px}
.notes-empty{font-size:13px;color:#a0aec0;margin-bottom:16px}
.notes-list{display:flex;flex-direction:column;gap:12px;margin-bottom:18px}
.note-item{background:#f9fbff;border-radius:10px;padding:12px 14px}
.note-meta{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
.note-author{font-size:12px;font-weight:700;color:#1a2b45}
.note-time{font-size:11px;color:#a0aec0}
.note-body{font-size:13px;color:#3d5068;margin:0;line-height:1.55;white-space:pre-wrap}
.note-form{margin-top:4px}
.note-textarea{resize:vertical;min-height:72px;margin-bottom:10px}
.note-form-actions{display:flex;justify-content:flex-end}
.header-actions{display:flex;align-items:center;gap:10px}
.export-wrap{position:relative}
.btn-export{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px;white-space:nowrap}
.btn-export:hover{background:#e8edf5}
.btn-export:disabled{opacity:.55;cursor:not-allowed}
.export-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1.5px solid #e4eaf2;border-radius:10px;box-shadow:0 6px 20px rgba(0,0,0,.1);z-index:200;min-width:160px;overflow:hidden}
.export-opt{display:flex;align-items:center;width:100%;padding:10px 14px;background:none;border:none;font-size:13px;color:#1a2b45;cursor:pointer;text-align:left}
.export-opt:hover{background:#f4f7fb}
.btn-apply{background:#004AAD;color:#fff;border:none;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-apply:hover{background:#003b8e}
.btn-clear{background:#f4f7fb;color:#3d5068;border:1.5px solid #e4eaf2;padding:9px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer}
.btn-clear:hover{background:#e8edf5}
.me-2{margin-right:6px}
.col-picker-backdrop{position:fixed;inset:0;background:rgba(10,20,40,.45);z-index:1100;display:flex;align-items:center;justify-content:center}
.col-picker-modal{background:#fff;border-radius:14px;width:480px;max-width:95vw;box-shadow:0 8px 40px rgba(0,74,173,.18);overflow:hidden}
.col-picker-header{display:flex;align-items:center;justify-content:space-between;padding:18px 22px 14px;border-bottom:1.5px solid #eaeff6;font-size:15px;font-weight:700;color:#1a2b45}
.col-picker-badge{background:#004AAD;color:#fff;border-radius:20px;padding:1px 9px;font-size:12px;font-weight:700;margin-left:8px}
.col-picker-close{background:none;border:none;cursor:pointer;color:#7a90b0;font-size:16px;line-height:1}
.col-picker-body{padding:16px 22px}
.col-picker-actions{display:flex;gap:14px;margin-bottom:12px}
.col-picker-link{background:none;border:none;color:#004AAD;font-size:13px;font-weight:600;cursor:pointer;padding:0}
.col-picker-link:hover{text-decoration:underline}
.col-picker-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px}
.col-picker-item{display:flex;align-items:center;gap:8px;font-size:13.5px;color:#1a2b45;cursor:pointer}
.col-picker-item input[type=checkbox]{width:15px;height:15px;accent-color:#004AAD;cursor:pointer}
.col-picker-footer{display:flex;justify-content:flex-end;gap:10px;padding:14px 22px 18px;border-top:1.5px solid #eaeff6}
.fade-enter-active,.fade-leave-active{transition:opacity .18s ease}
.fade-enter,.fade-leave-to{opacity:0}
</style>
